import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useVerifyPhoneRequest,
  useVerifyPhoneConfirm,
  useVerifyEmailRequest,
  useVerifyEmailConfirm,
} from "@orbit/api-client-react";
import { OtpVerificationStep } from "./OtpVerificationStep";

type Channel = "phone" | "email";

interface Props {
  channel: Channel;
  destination: string;
  onClose: () => void;
}

type Step =
  | { kind: "intro" }
  | { kind: "otp"; otpRequestId: string; expiresAt: string }
  | { kind: "done" };

export function VerifyChannelDialog({ channel, destination, onClose }: Props) {
  const qc = useQueryClient();
  const phoneReq = useVerifyPhoneRequest();
  const phoneConfirm = useVerifyPhoneConfirm();
  const emailReq = useVerifyEmailRequest();
  const emailConfirm = useVerifyEmailConfirm();

  const [step, setStep] = useState<Step>({ kind: "intro" });
  const [error, setError] = useState<string | null>(null);

  const requesting = channel === "phone" ? phoneReq.isPending : emailReq.isPending;

  async function startVerify() {
    setError(null);
    try {
      const r =
        channel === "phone"
          ? await phoneReq.mutateAsync()
          : await emailReq.mutateAsync();
      // The /request endpoints return a discriminated union:
      //   { otpRequestId, expiresAt, devOtp? }   ← OTP dispatched
      //   { alreadyVerified: true }              ← idempotent no-op
      // The latter happens when the channel was verified between the
      // user opening this dialog and clicking "Send code" (e.g. on
      // another device). We refresh /customer/me and short-circuit to
      // the success state instead of forcing a useless OTP step.
      if ("alreadyVerified" in r) {
        await qc.invalidateQueries({ queryKey: ["customer-me"] });
        setStep({ kind: "done" });
        return;
      }
      setStep({
        kind: "otp",
        otpRequestId: r.otpRequestId,
        expiresAt: r.expiresAt,
      });
    } catch (err) {
      const e = err as {
        status?: number;
        apiError?: { code?: string; message?: string };
      };
      const code = e.apiError?.code;
      if (e.status === 503) {
        setError(
          channel === "phone"
            ? "SMS service isn't configured yet. Tell your admin."
            : "Email service isn't configured yet. Tell your admin.",
        );
      } else if (code === "EMAIL_NOT_SET" || code === "EMAIL_NOT_ON_PROFILE") {
        // Server currently emits EMAIL_NOT_SET; the legacy alias is
        // kept so an older client/server pairing still gets the right
        // copy.
        setError("Add an email to your profile first, then verify it.");
      } else if (
        code === "PHONE_ALREADY_VERIFIED" ||
        code === "EMAIL_ALREADY_VERIFIED"
      ) {
        // Defensive: current server returns 200 + alreadyVerified
        // (handled above), but if a future revision starts erroring
        // out instead, surface the same friendly message rather than
        // the raw error envelope.
        setError(`Your ${channelLabel} is already verified.`);
      } else if (e.status === 429) {
        setError("Too many requests. Wait a minute and try again.");
      } else {
        setError(e.apiError?.message ?? "Couldn't send the code.");
      }
    }
  }

  async function confirmCode(code: string) {
    if (step.kind !== "otp") return;
    if (channel === "phone") {
      await phoneConfirm.mutateAsync({
        otpRequestId: step.otpRequestId,
        code,
      });
    } else {
      await emailConfirm.mutateAsync({
        otpRequestId: step.otpRequestId,
        code,
      });
    }
    await qc.invalidateQueries({ queryKey: ["customer-me"] });
    setStep({ kind: "done" });
  }

  const channelLabel = channel === "phone" ? "phone number" : "email";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Verify ${channelLabel}`}
      className="fixed inset-0 z-50 flex items-end justify-center bg-navy/40 sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-t-2xl bg-white p-6 shadow-xl sm:rounded-2xl">
        <div className="flex items-start justify-between">
          <h3 className="text-base font-semibold text-navy">
            Verify your {channelLabel}
          </h3>
          <button
            onClick={onClose}
            className="text-xs text-ink-dark underline"
            aria-label="Close"
          >
            Close
          </button>
        </div>

        {step.kind === "intro" && (
          <div className="mt-3 space-y-4">
            <p className="text-sm text-ink-dark">
              We&apos;ll send a 6-digit code to{" "}
              <span className="font-medium text-navy">{destination}</span>.
            </p>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="button"
              onClick={startVerify}
              disabled={requesting}
              className="btn-primary w-full"
            >
              {requesting ? "Sending code…" : "Send verification code"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="block w-full text-center text-xs text-ink-dark underline"
            >
              Not now
            </button>
          </div>
        )}

        {step.kind === "otp" && (
          <div className="mt-4">
            <OtpVerificationStep
              title={`Enter the code we sent`}
              body={
                <>
                  Sent to{" "}
                  <span className="font-medium">{destination}</span>.
                </>
              }
              expiresAt={step.expiresAt}
              busy={
                channel === "phone"
                  ? phoneConfirm.isPending
                  : emailConfirm.isPending
              }
              secondaryAction={{
                label: "Send a new code",
                onClick: () => {
                  setStep({ kind: "intro" });
                },
              }}
              onSubmit={confirmCode}
            />
          </div>
        )}

        {step.kind === "done" && (
          <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4 text-sm text-green-900">
            <p className="font-medium">
              Your {channelLabel} is verified.
            </p>
            <p className="mt-1 text-green-900/80">
              The verified badge will appear on your profile.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-3 text-xs font-medium underline"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
